import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Formik, Field } from 'formik';
import { navigate } from '@reach/router';
import { Button, ModalFooter } from 'reactstrap';
import * as signUpConstants from 'constants/signUp';
import * as currentUserActions from 'state/actions/currentUserActions';
import * as editProfileActions from 'state/actions/editProfileActions';
import * as currentUserSelectors from 'state/selectors/currentUserSelectors';
import ProfileForm from 'components/forms/profile-form/ProfileForm';
import getSuffixValidationSchema from 'components/forms/profile-form/getSuffixValidationSchema';
import getExternalIDValidationSchema from 'components/forms/profile-form/getExternalIDValidationSchema';
import getMiddleInitialValidationSchema from 'components/forms/profile-form/getMiddleInitialValidationSchema';
import getProfileValidationSchema from 'components/forms/profile-form/getProfileValidationSchema';
import getValidationSchema from 'components/forms/my-profile/getValidationSchema';
import { I18N, I18NContext, I18NConsumer, GlobalContext, EBModal } from 'commons';
import * as yup from 'yup';
import ReactGA from 'react-ga';
import { orgId, setPageTitle, Ajax, clientTimeZone, cookies } from 'utils';
import ProfileInformationCard from 'containers/sign-up/InformationCard';
import { selectShouldShowIncidentSub } from 'state/selectors/subscriptionSelectors';
import _ from 'lodash';
import AlertBox from 'components/forms/AlertBox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faInfoCircle, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import v from 'scss/v';
import ErrorMessage from '../../components/forms/ErrorMessage';
import SignUpHeader from './SignUpHeader';
import SignUpContent from './SignUpContent';
import getValidationSchemaForDropDown from 'components/forms/profile-form/validationSchemaForDropdown';
import { PREFERREDLANGUAGENOTPROVIDED } from 'constants/applicationConstant';

const Profile = ({
  saveMyProfile,
  getProfileInit,
  firstName,
  lastName,
  timeZone,
  suffix,
  middleInitial,
  externalId,
  showRegistrationLink,
  isEditMode,
  registrationEmail,
  contactPaths,
  isEmailEditable,
  isPreferredLanguageEditable,
  isPreferredLanguageMandatory,
  preferredLanguageList,
  minContactPathNum,
  shouldIncidentSubShow,
  isChangePassword,
  profilesData,
  profileTimeZones,
  preferredLanguage
}) => {
  const i18n = useContext(I18NContext);
  const {
    organization: {
      suffixSetting = {}, shouldShowPreferredLanguage, externalIDSetting = {}, middleInitialsSetting = {},
      emailMandatory, userStatus, orgName, defaultCountry = 'US', showAddress, showCustomfield,
      showAlertSubscription, showGroupSubscription, isFedRAMPCustomer
    },
    account: {
      id: contactId,
      name: username,
      isMigrated,
      registered,
      isLinkNoRegisteredUser
    },
    setIsInLoggedInEditProfileMode,
    isInLoggedInEditProfileMode,
  } = useContext(GlobalContext);

  const entryFromPublicPortal = cookies.get('entryFromPublicPortal', {
    path: `/${orgId}`
  });

  if (isEditMode) {
    setIsInLoggedInEditProfileMode(true);
  }
  const [contactPathError, setContactPathError] = useState({});
  const [isMinContactPathNumShow, setIsMinContactPathNumShow] = useState(false);
  const [isNeedValidation, setIsNeedValidation] = useState(true);
  const [changePwdModalOpen, setChangePwdModalOpen] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState(null);
  const [changePasswordResultMsg, setChangePasswordResultMsg] = useState(null);
  const [defaultTimeZone, setDefaultTimeZone] = useState(timeZone);
  const isTimeZoneExposed = _.get(profilesData, 'timeZone.expose', false);
  const isTimeZoneEditable = _.get(profilesData, 'timeZone.editable', false);
  const timeZoneDataProfile = _.find(profilesData, profile => profile.name === 'Time Zone');
  const isTimeZoneMandatory = _.get(timeZoneDataProfile, 'mandatory', false);
  const { suffixMandatory } = suffixSetting;
  const { externalIDMandatory } = externalIDSetting;
  const { middleInitialsMandatory } = middleInitialsSetting;
  const showRegistrationEmail = !_.isEmpty(profilesData.email);
  const profileValidationSchema = getProfileValidationSchema({ userStatus, showRegistrationEmail, emailMandatory });
  const validationSchema = getValidationSchema({ userStatus, isFedRAMPCustomer });
  const [isSavedSucceed, setSavedSucceed] = useState(false);

  const suffixValidationSchema = getSuffixValidationSchema({
    suffixMandatory,
  });
  const externalIDValidationSchema = getExternalIDValidationSchema({
    externalIDMandatory, contactId
  });
  const middleInitialValidationSchema = getMiddleInitialValidationSchema({
    middleInitialsMandatory,
  });
  const fromMobile = window.location.search.indexOf('from=member') !== -1;

  const tipProfileText = 'ssp.contactpath.profile.tip';
  const tipProfileTitle = 'new.registration.signup.create.account.label.profileInformation';

  const contactPathList = contactPaths.filter(contactPath => contactPath.type !== 'RECIP' && contactPath.expose);
  const minContactPath = contactPathList.length < minContactPathNum ? contactPathList.length : minContactPathNum;
  const isAllFieldMandatory = minContactPath === contactPathList.length;
  const newError = {};
  const updateContactPathError = (name, value) => {
    if (value === 'delete') {
      delete contactPathError[name];
    } else {
      newError[name] = value;
    }
    setContactPathError({
      ...contactPathError, ...newError
    });
  };

  useEffect(() => {
    if (!changePwdModalOpen) {
      setChangePasswordError(null);
    }
  }, [changePwdModalOpen]);

  useEffect(() => {
    ReactGA.pageview(`/newRegistration/profilePage/${orgId}`);
    getProfileInit(contactId);
  }, []);

  useEffect(() => {
    setTimeout(() => {
      setChangePasswordResultMsg(null);
    }, 10 * 1000);
  }, changePasswordResultMsg);

  useEffect(() => {
    if (timeZone) {
      setDefaultTimeZone(timeZone);
    } else if (profileTimeZones.findIndex(item => item.id === clientTimeZone) !== -1) {
      setDefaultTimeZone(clientTimeZone);
    }
  }, [timeZone, profileTimeZones]);

  let defaultProfileValues = {
    firstName: firstName || '',
    lastName: lastName || '',
    suffix: suffix || '',
    middleInitial: middleInitial || '',
    externalId: externalId || '',
    timeZone: defaultTimeZone || '',
  };
  if(shouldShowPreferredLanguage){
    defaultProfileValues.preferredLanguage = PREFERREDLANGUAGENOTPROVIDED;
  }

  if (showRegistrationEmail) {
    defaultProfileValues = { ...defaultProfileValues, email: registrationEmail || '' };
  }

  const requestChangePassword = (currentUsername, currentPassword, newPassword) => {
    const data = new FormData();
    data.append('loginId', currentUsername);
    data.append('oldPassword', currentPassword);
    data.append('newPassword', newPassword);

    const apiUrl = `/${orgId}/changePassword`;
    return Ajax.post(apiUrl, data);
  };

  const reqeustValidatePassword = (currentUsername, currentPassword) => {
    const reqData = new FormData();
    reqData.append('userName', currentUsername);
    reqData.append('password', currentPassword);
    return Ajax.post(`/${orgId}/changePassword/validatePassword`, reqData);
  };


  const preferredLanguageSchema = getValidationSchemaForDropDown({ 
      isFieldMandatory: isPreferredLanguageMandatory && isPreferredLanguageEditable,
      fieldName: 'preferredLanguage',
      fieldErrorString: `ssp.register.preferredLanguage`,
      defaultString: [PREFERREDLANGUAGENOTPROVIDED ,'Please select...']
  });

  const timeZoneValidationSchema = getValidationSchemaForDropDown({ 
    isFieldMandatory: isTimeZoneMandatory,
    fieldName: 'timeZone',
    fieldErrorString: `contact.path.device.time.frame.quiet.time.timezone`
  });

  const profileFormValidation = yup.object().shape({
    firstName: profileValidationSchema.firstName,
    ...middleInitialValidationSchema,
    lastName: profileValidationSchema.lastName,
    ...suffixValidationSchema,
    ...externalIDValidationSchema,
    ...timeZoneValidationSchema,
    ...(shouldShowPreferredLanguage ? preferredLanguageSchema : {}),
    ...profileValidationSchema.email ? {
      email: profileValidationSchema.email
    } : {},
  });

  return (
    <div className="sign-up profile-info container ssp-well">
      <I18NConsumer>
        {() => setPageTitle(`${orgName} - ${i18n['ssp.register.profile.title']}`)}
      </I18NConsumer>
      {
        !isEditMode && <SignUpHeader currentSection={signUpConstants.SIGN_UP_SECTIONS.profile.id} />
      }
      <I18N
        isInLoggedInEditProfileMode={isInLoggedInEditProfileMode}
        tag={SignUpContent}
        i18n-title={(isInLoggedInEditProfileMode) ? 'ssp.register.review.section.tilte.myProfile' : 'new.registration.signup.create.account.label.profileInformation'}
      >
        {
          /**
            Data from account api:
            registered: boolean
            isMigrated: boolean
            isLinkNoRegisteredUser: boolean

            Data from URL:
            fromMobile: boolean

            Data from profile api:
            showRegistrationLink: boolean
          */
          !registered && !isMigrated && !isLinkNoRegisteredUser && !fromMobile && showRegistrationLink &&
          <div className="row">
            <div className="col-md-8">
              <div className="altermate-account-link-box pt-4 m-2">
                <div className="alert alert-info" role="alert">
                  <FontAwesomeIcon icon={faInfoCircle} color={v('$blue-7')} width="1em" height="1em" />
                  <div className="ml-2">
                    <b><I18N>home.alert.box.title.info</I18N></b>
                    <p className="mb-0"><I18N>ssp.myprofile.register.note</I18N>
                      <button
                        type="button"
                        className="btn-link ml-1"
                        onClick={() => { navigate(`./new?contactId=${contactId}`, { state: { firstName, lastName, contactId, fromSSO: true } }); }}
                      >
                        <I18N>ssp.myprofile.register.note.link</I18N>
                      </button>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
        <div className="row">
          <div className="col-12">
            <I18N
              tag={ProfileInformationCard}
              className="pt-4 pl-2"
              i18n-tipTitle={tipProfileTitle}
              i18n-tipText={tipProfileText}
              i18n-instruction="form.fields.are.required"
            />
          </div>
          <div className="col-12 col-sm-8">
            <AlertBox message="home.alert.box.message.myProfile" isSavedSucceed={isSavedSucceed} />
            {
              isEditMode && registered && !isMigrated && !entryFromPublicPortal &&
                <div className="username-box pt-2 pl-2">
                  <div className="form-group row mb-0">
                    <span className="col-12 label" htmlFor="username" id="usernameLabel">
                      <I18N>ssp.register.username</I18N>
                    </span>
                    <div className="col-12 d-flex flex-row align-items-center">
                      <div id="username" aria-labelledby="usernameLabel" className="text-break notranslate">{username}</div>
                      {
                        isChangePassword &&
                        <div className="change-paaword-link-box mx-3">
                          <a
                            href="JavaScript:void(0);"
                            onClick={() => { setChangePwdModalOpen(true); }}
                            eb-test="ssp_profile_link_change_password"
                          >
                            <I18N>ssp.changepwd.title</I18N>
                          </a>
                        </div>
                      }
                      {
                        changePasswordResultMsg &&
                        <div id="changePasswordSuccessAlert" className="alert alert-success" role="alert" aria-live="assertive">
                          <FontAwesomeIcon icon={faCheckCircle} color={v('$green-7')} width="1em" height="1em" />
                          <div className="ml-2">
                            <b><I18N>home.alert.box.title.success</I18N></b>
                            <p className="mb-0"><I18N>ssp.password.change.success</I18N></p>
                          </div>
                        </div>
                      }
                    </div>
                  </div>
                </div>
            }
            <Formik
              validateOnBlur={false}
              validateOnChange={false}
              initialValues={{
                ...defaultProfileValues,
              }}
              validationSchema={profileFormValidation}
            >
              {({
                values,
                touched,
                errors,
                setFieldTouched,
                setFieldValue,
                setFieldError,
                validateField,
              }) => (

                <ProfileForm
                  isEditMode={isEditMode}
                  isInLoggedInEditProfileMode={isInLoggedInEditProfileMode}
                  isMinContactPathNumShow={isMinContactPathNumShow}
                  setIsMinContactPathNumShow={setIsMinContactPathNumShow}
                  isNeedValidation={isNeedValidation}
                  setIsNeedValidation={setIsNeedValidation}
                  firstName={firstName}
                  lastName={lastName}
                  timeZone={defaultTimeZone}
                  registrationEmail={registrationEmail}
                  contactId={contactId}
                  orgCountry={defaultCountry}
                  showAddress={showAddress}
                  showCustomfield={showCustomfield}
                  showAlertSubscription={showAlertSubscription}
                  showGroupSubscription={showGroupSubscription}
                  showIncidentSub={shouldIncidentSubShow}
                  values={values}
                  suffix={suffix}
                  middleInitial={middleInitial}
                  externalId={externalId}
                  touched={touched}
                  errors={errors}
                  minContactPathNum={minContactPath}
                  isAllFieldMandatory={isAllFieldMandatory}
                  contactPathError={contactPathError}
                  setContactPathError={updateContactPathError}
                  setFieldError={setFieldError}
                  onSubmit={(profile, contactPaths, successCallback, failCallback) => saveMyProfile(contactId, { profile, contactPaths, successCallback, failCallback })}
                  setFieldTouched={setFieldTouched}
                  setFieldValue={setFieldValue}
                  validateField={validateField}
                  profileValidation={profileFormValidation}
                  profilesData={profilesData}
                  setSavedSucceed={setSavedSucceed}
                  isEmailEditable={isEmailEditable}
                  contactPaths={contactPaths}
                  profileTimeZones={profileTimeZones}
                  preferredLanguage={preferredLanguage}
                  isPreferredLanguageEditable={isPreferredLanguageEditable}
                  isPreferredLanguageMandatory={isPreferredLanguageMandatory}
                  preferredLanguageList={preferredLanguageList}
                  isTimeZoneExposed={isTimeZoneExposed}
                  isTimeZoneEditable={isTimeZoneEditable}
                  isTimeZoneMandatory={isTimeZoneMandatory}
                />
              )}
            </Formik>
          </div>
        </div>
      </I18N>
      <EBModal
        className="change-password-modal"
        title={i18n.get('ssp.changepwd.title')}
        type="confirm"
        isOpen={changePwdModalOpen}
        toggleModal={() => {
          setChangePwdModalOpen(false);
        }}
        showFooter={false}
        backdrop="static"
      >
        <Formik
          initialValues={{ currentPassword: '', newPassword: '', confirmPassword: '' }}
          validationSchema={
            yup.object().shape({
              currentPassword: yup.string().required('jquery.validation.field.required, global.forgot.password.current.password'),
              newPassword: validationSchema.password.required('jquery.validation.field.required, ssp.changepwd.newpwd'),
              confirmPassword: yup.string().oneOf([yup.ref('newPassword')], 'ssp.password.confirm.invalid').required('jquery.validation.field.required, ssp.register.confirmpassword'),
            })
          }
          onSubmit={(values, { setFieldError }) => {
            setChangePasswordError(null);
            const { currentPassword, newPassword } = values;

            reqeustValidatePassword(username, currentPassword).then(({ data: isValid }) => {
              if (isValid) {
                requestChangePassword(username, currentPassword, newPassword).then(({ data: res }) => {
                  if (res.status == '1') {
                    setChangePasswordResultMsg('ssp.password.change.success');
                    setChangePwdModalOpen(false);
                  } else if (res.errorCode) {
                    setChangePasswordError(res.errorCode);
                  } else {
                    setChangePasswordError('ssp.password.change.failed');
                  }
                });
              } else {
                setFieldError('currentPassword', 'ssp.password.valid.invalid');
                document.getElementById('currentPassword').focus();
              }
            });
          }}
        >
          {({
            touched,
            errors,
            handleSubmit
          }) => {
            return (
              <form onSubmit={handleSubmit}>
                <p className="text-muted form-group"><I18N>form.fields.are.required</I18N></p>
                {
                  changePasswordError &&
                <div className="form-group">
                  <div className="alert alert-danger text-left" role="alert">
                    <FontAwesomeIcon icon={faExclamationCircle} color={v('$red-7')} width="1em" height="1em" />
                    <div className="ml-2">
                      <b><I18N>home.alert.box.title.error</I18N></b>
                      <p className="mb-0"><I18N>{changePasswordError}</I18N></p>
                    </div>
                  </div>
                </div>
                }
                <div className="form-group">
                  <label id="currentPasswordLabel" htmlFor="currentPassword">

                    <I18N>ssp.changepwd.oldpwd</I18N>
                    <span aria-hidden="true" className="required-star">*</span>
                    <span className="sr-only"><I18N>information.label.required</I18N></span>
                  </label>
                  <Field
                    type="password"
                    name="currentPassword"
                    id="currentPassword"
                    autoComplete="off"
                    className="form-control"
                    aria-describedby="currentPasswordLabel currentPasswordError"
                    aria-invalid={touched.currentPassword && !!errors.currentPassword}
                  />
                  {
                    touched.currentPassword && errors.currentPassword &&
                  <ErrorMessage id="currentPasswordError" role="alert" aria-live="assertive">{errors.currentPassword}</ErrorMessage>
                  }
                </div>
                <div className="form-group">
                  <label id="newPasswordLabel" htmlFor="newPassword">
                    <I18N>ssp.changepwd.newpwd</I18N>
                    <span aria-hidden="true" className="required-star">*</span>
                    <span className="sr-only"><I18N>information.label.required</I18N></span>
                  </label>
                  <div className="alert alert-light" id="passwordNotice">
                    {
                      (isFedRAMPCustomer) ? <I18N>aware.register.passwordNotice.fedRamp</I18N> : (userStatus === 'public') ? <I18N>aware.register.passwordNotice</I18N> : <I18N>aware.register.passwordNotice2</I18N>
                    }
                  </div>
                  <Field
                    type="password"
                    name="newPassword"
                    id="newPassword"
                    autoComplete="off"
                    className="form-control"
                    aria-describedby="passwordNotice newPasswordError"
                    aria-invalid={touched.newPassword && !!errors.newPassword}
                  />
                  {
                    touched.newPassword && errors.newPassword &&
                  <ErrorMessage id="newPasswordError" role="alert" aria-live="assertive">{errors.newPassword}</ErrorMessage>
                  }
                </div>
                <div className="form-group">
                  <label id="confirmPasswordLabel" htmlFor="confirmPassword">
                    <I18N>ssp.register.confirmpassword</I18N>
                    <span aria-hidden="true" className="required-star">*</span>
                    <span className="sr-only"><I18N>information.label.required</I18N></span>
                  </label>
                  <Field
                    type="password"
                    name="confirmPassword"
                    id="confirmPassword"
                    autoComplete="off"
                    className="form-control"
                    aria-describedby="confirmPasswordLabel confirmPasswordError"
                    aria-invalid={touched.confirmPassword && !!errors.confirmPassword}
                  />
                  {
                    touched.confirmPassword && errors.confirmPassword &&
                  <ErrorMessage id="confirmPasswordError" role="alert" aria-live="assertive">{errors.confirmPassword}</ErrorMessage>
                  }
                </div>
                <ModalFooter>
                  <Button value={i18n['global.dialog.button.cancel']} color="secondary" onClick={() => setChangePwdModalOpen(false)} eb-test="ssp_change_password_button_cancel">
                    <I18N>global.dialog.button.cancel</I18N>
                  </Button>
                  <Button value={i18n['button.save']} color="primary" type="submit" eb-test="ssp_change_password_button_submit">
                    <I18N>button.save</I18N>
                  </Button>
                </ModalFooter>
              </form>
            );
          }}
        </Formik>
      </EBModal>
    </div>
  );
};

Profile.defaultProps = {
  timeZone: '',
  suffix: '',
  middleInitial: '',
  externalId: '',
  isEditMode: false,
  minContactPathNum: 0,
  contactPaths: [{}],
};

Profile.propTypes = {
  getProfileInit: PropTypes.func.isRequired,
  saveMyProfile: PropTypes.func.isRequired,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  timeZone: PropTypes.string,
  suffix: PropTypes.string,
  middleInitial: PropTypes.string,
  externalId: PropTypes.string,
  registrationEmail: PropTypes.string.isRequired,
  isEditMode: PropTypes.bool,
  minContactPathNum: PropTypes.number,
  contactPaths: PropTypes.arrayOf(PropTypes.shape()),
  shouldIncidentSubShow: PropTypes.bool.isRequired,
  isChangePassword: PropTypes.bool.isRequired,
  profileTimeZones: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  isPreferredLanguageEditable: PropTypes.bool.isRequired,
  isEmailEditable: PropTypes.bool.isRequired,
  preferredLanguage: PropTypes.string,
  preferredLanguageList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    displayLabel: PropTypes.string.isRequired,
  })),
};

const mapStateToProps = state => ({
  firstName: currentUserSelectors.selectFirstName(state),
  lastName: currentUserSelectors.selectLastName(state),
  timeZone: currentUserSelectors.selectTimeZone(state),
  middleInitial: currentUserSelectors.selectMiddleInitial(state),
  suffix: currentUserSelectors.selectSuffix(state),
  externalId: currentUserSelectors.selectExternalId(state),
  showRegistrationLink: currentUserSelectors.selectShowRegistrationLink(state),
  registrationEmail: currentUserSelectors.selectEmail(state),
  minContactPathNum: currentUserSelectors.selectMinContactPathNum(state),
  contactPaths: currentUserSelectors.selectContactPaths(state),
  isChangePassword: currentUserSelectors.selectIsChangePassword(state),
  shouldIncidentSubShow: selectShouldShowIncidentSub(state),
  profilesData: currentUserSelectors.selectProfiles(state),
  profileTimeZones: currentUserSelectors.selectTimezones(state),
  isPreferredLanguageEditable: currentUserSelectors.selectIsPreferredLanguageEditable(state),
  isPreferredLanguageMandatory: currentUserSelectors.selectIsPreferredLanguageMandatory(state),
  isEmailEditable: currentUserSelectors.selectIsEmailEditable(state),
  preferredLanguage: currentUserSelectors.selectPreferredLanguage(state, true),
  preferredLanguageList: currentUserSelectors.selectPreferredLanguageList(state),
});

const mapDispatchToProps = dispatch => ({
  getProfileInit: (contactId) => dispatch(currentUserActions.getProfileInit.action({ contactId })),
  saveMyProfile: (contactId, data) =>
    dispatch(
      editProfileActions.saveMyProfile.action({ contactId, ...data }),
    ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Profile);
